import { useMemo, useReducer } from 'react';

import type { ContactDecisionMakerFlowActions, ContactDecisionMakerFlowModel } from '../../types';

import { contactDecisionMakerFlowReducer, INITIAL_CONTACT_DECISION_MAKER_FLOW_STATE } from './reducer';

/**
 * Owns the compose flow's stage machine.
 *
 * State lives here rather than inside the rendered stage so that editing the story or the draft
 * doesn't churn the identity of the component holding the caret — the same trap `useShareCopyEditor`
 * documents on the PSF slide-up, where a per-keystroke remount dropped the cursor mid-word.
 */
export function useContactDecisionMakerFlow(): ContactDecisionMakerFlowModel {
	const [state, dispatch] = useReducer(contactDecisionMakerFlowReducer, INITIAL_CONTACT_DECISION_MAKER_FLOW_STATE);

	const actions = useMemo<ContactDecisionMakerFlowActions>(
		() => ({
			open: () => dispatch({ type: 'open' }),
			dismiss: () => dispatch({ type: 'dismiss' }),
			pickReason: (reasonId) => dispatch({ type: 'pickReason', reasonId }),
			backToReasons: () => dispatch({ type: 'backToReasons' }),
			setStory: (story) => dispatch({ type: 'setStory', story }),
			requestDraft: () => dispatch({ type: 'requestDraft' }),
			draftReady: (draft, source) => dispatch({ type: 'draftReady', draft, source }),
			writeOwnDraft: (seededDraft) => dispatch({ type: 'writeOwnDraft', seededDraft }),
			editDraft: () => dispatch({ type: 'editDraft' }),
			setDraft: (draft) => dispatch({ type: 'setDraft', draft }),
			finishEditing: () => dispatch({ type: 'finishEditing' }),
			send: () => dispatch({ type: 'send' }),
			sendFailed: (failure) => dispatch({ type: 'sendFailed', failure }),
			sendSucceeded: () => dispatch({ type: 'sendSucceeded' }),
			openShare: () => dispatch({ type: 'openShare' }),
			closeShare: () => dispatch({ type: 'closeShare' }),
			finish: () => dispatch({ type: 'finish' }),
		}),
		[],
	);

	return { data: state, actions };
}
